import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

import Box from "ds/components/Box";
import TreeList from "ds/components/TreeList";
import useTypedContext from "hooks/useTypedContext";

import { ProviderVersionDocsContext } from "./Context";
import DocContent from "./DocContent";
import { findDocById, ProviderDocTreeNode } from "./helpers";
import styles from "./styles.module.css";

const Overview = () => {
  const { docs, provider, version } = useTypedContext(ProviderVersionDocsContext);
  const location = useLocation();
  const navigate = useNavigate();

  const resourceSlug = useMemo(() => {
    const basePath = `/provider/${provider.id}/version/${version.number}/docs`;
    // Handle both /docs and /docs/something
    const relativePath = location.pathname === basePath
      ? ""
      : location.pathname.replace(`${basePath}/`, "");
    return relativePath || "index"; // Default to index (overview) when no doc is selected
  }, [location.pathname, provider.id, version.number]);

  const selectedDoc = useMemo(() => findDocById(docs, resourceSlug), [docs, resourceSlug]);

  const handleDocSelect = useCallback(
    (id: string) => {
      const doc = docs.find((d) => d.id === id);

      if (doc) {
        navigate(`/provider/${provider.id}/version/${version.number}/docs/${id}`);
      }
    },
    [docs, navigate, provider.id, version.number]
  );

  return (
    <Box className={styles.sidebarContent} grow="1">
      <div className={styles.sidebar}>
        <TreeList<ProviderDocTreeNode>
          ariaLabel="Documentation navigation"
          data={docs}
          idKey="id"
          parentKey="parentId"
          nameKey="name"
          selectedKey={resourceSlug}
          onChange={handleDocSelect}
        />
      </div>

      <Box direction="column" fullWidth zeroMinWidth>
        <DocContent
          selectedDoc={selectedDoc}
          providerId={provider.id}
          versionNumber={version.number}
        />
      </Box>
    </Box>
  );
};

export default Overview;
